<template>
  <div>
    <v-skeleton-loader v-show="!loadComplete" type="article" class="mx-auto mt-9 pt-9" />
    <div v-show="loadComplete">
      <BaseForm
        v-bind="{ ...prefillQueryParams, userIsLoggedIn }"
        ref="baseForm"
        @load-from-store-complete="loadComplete = true"
        @set-selected-brand="setSelectedBrand"
      ></BaseForm>

      <BaseAlert
        v-if="userIsLoggedIn"
        class="py-4 mt-2 mb-8 mt-md-3"
        :always-show="true"
        type="warning"
        icon="info"
        outlined
      >
        <template #additionalMessageRow>
          <div class="d-flex flex-column flex-sm-row align-center">
            <p class="mb-0">{{ $t('hubspot.alertToMyData') }}</p>
            <v-btn
              class="mt-4 ml-4 btn mt-sm-0"
              outlined
              color="primary"
              rounded
              @click="handleClick"
              >{{ $t('hubspot.myData') }}</v-btn
            >
          </div>
        </template>
      </BaseAlert>

      <v-dialog ref="editDialog" v-model="editDialog" max-width="1200" scrollable persistent>
        <UsermanagementEdit
          v-if="editDialog"
          :addresses="addresses"
          :templates="[]"
          :errors="saveUserError"
          :pending="requestPending"
          :self-edit="true"
          :user="currentUser"
          @save="saveSelf"
          @close="editDialog = false"
          @update-user="updateSelf"
        />
      </v-dialog>

      <HubSpotWizard
        class="pb-8"
        :inital-steps="initialWizardSteps"
        :brand="selectedBrand"
        @hideSubmitButton="toggleSubmitButton"
        @showAdditionalFields="toggleAdditionalFields"
        @jumpToPageKeys="setJumpToPageKeys"
        @resetOutcomeMessage="resetOutcomeMessage"
      />

      <component
        :is="component"
        v-if="componentName"
        v-bind="{ ...prefillQueryParams, showAdditionalFields, jumpToPageKeys }"
        ref="dynamicForm"
        :brand="selectedBrand"
      ></component>

      <DescriptionForm v-if="component && !hideSubmitButton" ref="descriptionForm" />

      <div>
        <v-btn
          v-if="component && !hideSubmitButton"
          class="ml-auto btn mr-none"
          dense
          rounded
          elevation="0"
          color="primary"
          type="submit"
          style="display: block"
          @click="handleSubmit"
        >
          <template v-if="fetching">
            <v-progress-circular :size="20" :indeterminate="fetching" />
          </template>
          <template v-else>
            {{ $t('hubspot.submit') }}
          </template>
        </v-btn>
      </div>

      <div class="mt-9" :class="$style.mandatoryMessage">
        {{ $t('form.mandatoryField') }}
      </div>
      <FormResultMessage
        :show-general-error-message="showGeneralErrorMessage"
        :show-success-message="showSuccessMessage"
        :fetching="fetching"
        :general-error-message="$t('hubspot.generalError')"
        :success-message="$t('hubspot.successMessage')"
      />
    </div>
  </div>
</template>

<script>
import ContainerElement from '@/js/components/Shared/ContainerElement.vue'
import BaseForm from '@/js/components/HubSpot/Forms/BaseForm.vue'
import DescriptionForm from '@/js/components/HubSpot/Forms/DescriptionForm.vue'
import HubSpotWizard from '@/js/components/HubSpot/HubSpotWizard.vue'
import forms from '@/js/components/HubSpot/Forms/hubspot-forms'
import HubSpotFormMixin from '@/js/mixins/hubspot-form-mixin.js'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'

import MyDataMixin from '@/js/components/Usermanagement/my-data-mixin.js'
import UsermanagementEdit from '@/js/components/Usermanagement/UsermanagementEdit.vue'
import { mapGetters } from 'vuex'
import { types as STORE_MODULES } from '@/js/plugins/store/store-modules.js'
import APP_GETTER from '@/js/plugins/store/modules/app/getter-types.js'
import FormResultMessage from '@/js/components/Forms/FormResultMessage.vue'

export default {
  name: 'HubSpotFormRenderer',
  components: {
    ContainerElement,
    IconSelect,
    ...forms,
    BaseForm,
    DescriptionForm,
    HubSpotWizard,
    UsermanagementEdit,
    FormResultMessage,
  },
  mixins: [HubSpotFormMixin, MyDataMixin],
  props: {
    initialWizardSteps: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      showGeneralErrorMessage: false,
      showSuccessMessage: false,
      componentName: this.$route?.query?.formName,
      loadComplete: false,
      hideSubmitButton: false,
      showAdditionalFields: false,
      selectedBrand: '',
      jumpToPageKeys: undefined,
      editDialog: false,
    }
  },
  computed: {
    ...mapGetters(STORE_MODULES.APP, {
      isSmWidth: APP_GETTER.IS_SM_WIDTH,
      user: APP_GETTER.USER,
    }),
    userIsLoggedIn() {
      return Object.keys(this.user).length !== 0
    },
    component() {
      // only render component which is in export "forms"
      return this.componentName in forms ? this.componentName : undefined
    },
    routeQuery() {
      return this.$route.query
    },
    prefillQueryParams() {
      // used to get params with prefix "prefill" and passed to forms
      const queryParams = this.$route.query
      const prefilleQueryParamsObj = {}

      for (let prefixKey in queryParams) {
        if (prefixKey.startsWith('prefill')) {
          prefilleQueryParamsObj[prefixKey] = queryParams[prefixKey]
        }
      }

      return prefilleQueryParamsObj
    },
  },
  watch: {
    routeQuery: {
      handler(nextProps, prevProps) {
        // if the formName changes update internal state to render correct form
        if (nextProps.formName !== prevProps?.formName) {
          this.setFormName(nextProps.formName)
        }
      },
      immediate: true,
    },
  },
  methods: {
    async handleSubmit() {
      this.$refs.baseForm.submit()
      this.$refs.dynamicForm.submit()
      this.$refs.descriptionForm.submit()

      this.$nextTick(() => {
        const firstError = this.$el.querySelector('.error--text')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })

      if (
        this.$refs.baseForm.isValid &&
        this.$refs.dynamicForm.isValid &&
        this.$refs.descriptionForm.isValid
      ) {
        const dataBase = this.$refs.baseForm.postData
        const dataDynamic = this.$refs.dynamicForm.postData
        const dataDescription = this.$refs.descriptionForm.postData

        if (dataBase.data.fields && dataDynamic.data.fields && dataDescription.data.fields) {
          const fields = [
            ...dataBase.data.fields,
            ...dataDynamic.data.fields,
            ...dataDescription.data.fields,
          ]

          const files = []

          if (dataDynamic.files) {
            files.push(...dataDynamic.files)
          }

          if (dataDescription.files) {
            files.push(...dataDescription.files)
          }

          const data = {
            data: {
              fields: fields,
            },
            files: files,
          }

          await this.submitCallback(data)

          const submitScrollAnchor = this.$el.querySelector(
            '.HubSpot-HubSpotFormRenderer__errorMessage',
          )
          if (submitScrollAnchor) {
            submitScrollAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }
      }
    },
    setFormName(formName) {
      // Reset form or set valid form
      if (formName === undefined || formName in forms) {
        // this sets internal componentName if modified by wizard
        this.componentName = formName
      }
    },
    setSelectedBrand(brand) {
      this.selectedBrand = brand
    },
    toggleSubmitButton(value) {
      this.hideSubmitButton = value
    },
    toggleAdditionalFields(value) {
      this.showAdditionalFields = value
    },
    setJumpToPageKeys(value) {
      this.jumpToPageKeys = value
    },
    resetOutcomeMessage() {
      if (this.showSuccessMessage === true) {
        this.showSuccessMessage = false
      }
      if (this.showGeneralErrorMessage === true) {
        this.showGeneralErrorMessage = false
      }
    },
    async handleClick() {
      await this.getCustomer(true)
      this.setInitialDealerInfos()
      this.editDialog = true
    },
  },
}
</script>

<style lang="scss" module>
.mandatoryMessage {
  padding: 1rem 0;

  color: var(--red);

  fill: var(--red);
  stroke: var(--red);
}

:global(.requiredHubspotFormInput label:before) {
  color: var(--error);

  content: '* ';
}

:global(.requiredHubspotFormInput label label:before) {
  display: none;
}
</style>
